import { useCallback, useEffect, useRef, useState } from 'react';
import { describeLoadError, type LoadError } from '../../../utils/loadError';

export interface AreaListState<T> {
  data: T[] | null;
  loading: boolean;
  error: LoadError | null;
  reload: () => Promise<void>;
}

/**
 * One area list (service / foreman / contract) with its own loading and error state (W-M17).
 *
 * Each tab renders as soon as its own list arrives instead of the whole page waiting behind a
 * spinner for the slowest one (contract areas). A failure is kept as an error - never turned into
 * an empty list - so the tab shows "Could not load ... - Retry" rather than "No contract areas
 * found" and a count of 0.
 *
 * Loads once on mount. It used to reload all three lists every time the tab changed, which is
 * what sent each area request again on every tab switch.
 */
export function useAreaList<T>(fetcher: () => Promise<T[]>, label: string): AreaListState<T> {
  const [data, setData] = useState<T[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LoadError | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const mounted = useRef(true);
  const requestId = useRef(0);

  const reload = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      if (!mounted.current || id !== requestId.current) return;
      if (!Array.isArray(result)) throw new Error(`Unexpected ${label} response`);
      setData(result);
    } catch (err) {
      if (!mounted.current || id !== requestId.current) return;
      setError(describeLoadError(err, label));
    } finally {
      if (mounted.current && id === requestId.current) setLoading(false);
    }
  }, [label]);

  useEffect(() => {
    mounted.current = true;
    void reload();
    return () => {
      mounted.current = false;
    };
  }, [reload]);

  return { data, loading, error, reload };
}
